import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { mountDevTools } from '../utils/mount-dev-tools';

import { TBffFacilityToVetspire } from '@src/components/AppointmentSelection/AppointmentSelection.helpers';
import { Location } from '@src/lib/__generated/vetspire/sdk';
import { deserializeDates, safeDeserialize } from '@src/store/utils/serialize-dates';

export interface LocationAppointmentCard {
	facility: TBffFacilityToVetspire;
	appointmentsLoaded: boolean;
}

export type State = {
	potentialFacilities: LocationAppointmentCard[];
	searchDate: Date;
	dayRange: number;
	appointmentId: string;
	dayRangeLimit: number | null;
	fromPAO: boolean;
	sessionGuid: string;
};

export type Actions = {
	addPotentialFacilitiy: (potentialFacility: Location | null) => void;
	setDayRange: (dayRange: number) => void;
	resetPotentialFacilities: () => void;
	setSearchDate: (searchDate: Date) => void;
	setAppointmentId: (appointmentId: string) => void;
	setDayRangeLimit: (dayRangeLimit: number | null) => void;
	setFromPAO: (fromPAO: boolean) => void;
	reset: () => void;
};

const makeInitialState = (): State => ({
	potentialFacilities: [],
	searchDate: new Date(),
	dayRange: 3,
	appointmentId: '',
	dayRangeLimit: null,
	fromPAO: false,
	sessionGuid: crypto.randomUUID(),
});

const useAppointmentSelectionStore = create<State & Actions>()(
	persist(
		(set, get) => ({
			...makeInitialState(),
			addPotentialFacilitiy: (potentialFacility) => {
				if (
					!potentialFacility ||
					get().potentialFacilities.some((facility) => facility.facility.id === potentialFacility.id)
				) {
					return;
				}

				const appointmentCardDefaults = {
					appointmentsLoaded: false,
				};

				set({
					potentialFacilities: [
						...get().potentialFacilities,
						{ facility: potentialFacility, ...appointmentCardDefaults },
					],
				});
			},
			setDayRange: (dayRange) => set({ dayRange }),
			resetPotentialFacilities: () => set({ potentialFacilities: [] }),
			setSearchDate: (searchDate) => {
				// Ensure we're setting a valid date
				const validDate = searchDate && !Number.isNaN(searchDate.getTime()) ? searchDate : new Date();
				set({ searchDate: validDate });
			},
			setAppointmentId: (appointmentId) => set({ appointmentId }),
			reset: () => set(makeInitialState()),
			setDayRangeLimit: (dayRangeLimit) => set({ dayRangeLimit }),
			setFromPAO: (fromPAO) => set({ fromPAO }),
		}),
		{
			name: 'book-an-appointment-appointment-selection',
			storage: {
				getItem: (name) => {
					const str = sessionStorage.getItem(name);
					if (!str) return null;
					return safeDeserialize(
						str,
						(state) => deserializeDates(state, ['searchDate' as keyof State], makeInitialState()),
						makeInitialState()
					);
				},
				setItem: (name, value) => sessionStorage.setItem(name, JSON.stringify(value)),
				removeItem: (name) => sessionStorage.removeItem(name),
			},
		}
	)
);

mountDevTools('useAppointmentSelectionStore', useAppointmentSelectionStore);

export default useAppointmentSelectionStore;
